import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LaravelPagination, Property } from '@app/shared/interfaces';

import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { environment } from '@env/environment';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ProductCatalogService, ProductItem } from '../../product-catalog/services/product-catalog.service';
import { DirectPlan } from '../models/direct-subscription.interface';
import { DirectSubscriptionService } from './direct-subscription.service';

export enum PayoutMethodProvider {
  STRIPE_OAUTH = 'stripe-oauth',
  ADYEN = 'adyen',
}

export enum AdyenPayoutMethodStatus {
  PENDING = 'pending',
  INVALID = 'invalid',
  VALID = 'valid',
  REJECTED = 'rejected',
}

export interface PayoutMethod {
  uuid: string;
  name: string;
  derived_name?: string;
  is_default: boolean;
  property_scope: number[]; // Array of property IDs
  all_properties: boolean;
  property_list: { id: number; name: string; picture: string }[];
  enabled: boolean;
  provider: PayoutMethodProvider;
  can_delete: boolean;
  scopes?: {
    [key: string]: {
      all_resources: boolean;
      ids: string[];
    };
  };
  adyen: {
    kyc_required: boolean;
    legal_name: string;
    status: AdyenPayoutMethodStatus;
    verification_errors: string[];
    account_holder_id: string;
    bank_account_number: string | null;
    missing_tin: boolean;
  } | null;
  stripe: {
    public_key: string;
    secret_key: string;
  } | null;
  balance_account?: {
    currency: string;
    account_holder: {
      legal_entity: {
        transfer_instruments: {
          metadata: {
            bankAccount: {
              bankName: string;
            };
          };
        }[];
      };
    };
  };
  platform_currency?: string;
}

export interface PayoutHistoryItem {
  team_id: number;
  identifier: string;
  payout_id: string | null;
  payout_date: string; // ISO 8601 string
  payout_method: {
    id: number;
    uuid: string;
    name: string | null;
    derived_name: string | null;
  };
  status: 'scheduled' | 'initiated' | 'confirmed' | 'failed' | 'returned';
  total_amount: {
    amount: number;
    amount_minor: number;
    currency: string;
    formatted_string: string;
    formatted_decimal: string;
  };
  currency: string;
  failed_retry_scheduled?: boolean;
  debug_info: {
    [key: string]: string;
  };
  intents: Array<{
    uuid: string;
    payout_uuid: string | null;
    transaction_code: string;
    transaction_type: string;
    platform: string;
    reference_id: string | null;
    reference_type: string | null;
    description: string | null;
    guest_name: string | null;
    amount: {
      amount: number;
      amount_minor: number;
      currency: string;
      formatted_string: string;
      formatted_decimal: string;
    };
    debug_info: {
      [key: string]: string;
    };
  }>;
}

export interface PayoutHistoryResponse {
  data: PayoutHistoryItem[];
  meta: LaravelPagination;
}

export interface AdyenPayoutMethodCreateRequest {
  nickname?: string | null;
  legal_entity: 'individual' | 'organization';
  first_name?: string; // Only required for legal_entity individual
  last_name?: string; // Only required for legal_entity individual
  legal_name?: string; // Only required for legal_entity organization
  country: string; // US only for now
  phone: string; // +1XXXXXXXXXX
  is_default: boolean;
  all_properties: boolean;
  properties: Array<number>;
  resources?: Array<{
    type: string;
    all_resources: boolean;
    resource_ids: string[];
  }>;
  uuid?: string;
  provider?: PayoutMethodProvider; // only required for the new FS endpoint
}

export interface PayoutMethodMonolithEditRequest {
  nickname: string;
  is_default: boolean;
  all_properties: boolean;
  properties: Property['id'][];
  resources?: Array<{
    type: string;
    all_resources: boolean;
    resource_ids: string[];
  }>;
}

export interface PayoutMethodVerifyInvitationRequest {
  email: string;
}

@Injectable({
  providedIn: 'root',
})
export class PayoutMethodsService {
  bookingApiUrl = environment.bookingApiUrl;
  apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private segment: SegmentIoService,
    private propertiesService: PropertiesService,
    private directSubscriptionService: DirectSubscriptionService,
    private productCatalogService: ProductCatalogService
  ) {}

  getPayoutMethod(uuid: string): Observable<PayoutMethod> {
    return this.http.get(`${this.apiUrl}/settings/payout-methods/${uuid}`).pipe(map((res: any) => res.data));
  }

  getPayoutMethodByResourceTypeAndResourceId(resourceType: string, resourceId: string): Observable<PayoutMethod> {
    return this.http.get(`${this.apiUrl}/settings/payout-methods/for/${resourceType}/${resourceId}`).pipe(map((res: any) => res.data));
  }

  fetchPayoutMethods(provider?: PayoutMethodProvider): Observable<PayoutMethod[]> {
    return this.http.get(`${this.apiUrl}/settings/payout-methods`).pipe(
      tap((res: any) => {
        const payoutMethods = res.data;
        const properties = [];

        if (!payoutMethods || payoutMethods.length === 0) {
          return;
        }

        // Filter by provider if specified
        if (provider) {
          res.data = payoutMethods.filter((payout) => payout.provider === provider);
        }

        // Custom sort function
        if (provider) {
          res.data.sort((a, b) => {
            if (a.provider === provider && b.provider !== provider) {
              return -1;
            } else if (b.provider === provider && a.provider !== provider) {
              return 1;
            } else {
              return 0;
            }
          });
        }

        res.data.forEach((payout) => {
          if (payout.property_scope?.length > 0) {
            properties.push(...payout.property_scope);
          }
        });

        res.properties = properties;

        res.payoutMethods = res.data;
        delete res.data;
      }),
      switchMap((res) => {
        return this.propertiesService
          .getProperties({
            filterCriteria: [],
            paginate: false,
            transformer: 'simple',
            idList: res.properties,
          })
          .pipe(
            map((inner) => {
              res.properties = inner.data;
              return res;
            })
          );
      }),
      tap((res) => {
        if (res.payoutMethods?.length > 0) {
          res.payoutMethods.forEach((payout) => {
            if (!payout.property_list) {
              payout.property_list = [];
            }

            res.properties.forEach((property) => {
              if (payout.property_scope.indexOf(property.id) > -1) {
                payout.property_list.push(property);
              }
            });
          });
        }
      }),
      map((res) => (res.payoutMethods ? res.payoutMethods : []))
    );
  }

  fetchPayoutHistory(pageNum): Observable<PayoutHistoryResponse> {
    return this.http
      .get<PayoutHistoryResponse>(`${this.apiUrl}/settings/payout-history`, {
        params: {
          page: pageNum.toString(),
        },
      })
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching payout history:', error);
          return of({ data: [], meta: null });
        })
      );
  }

  createStripeOauthPayoutMethod(code: string) {
    return this.http
      .post(`${this.apiUrl}/settings/payout-methods`, {
        provider: PayoutMethodProvider.STRIPE_OAUTH,
        code,
      })
      .pipe(
        map((res: any) => {
          return res.data;
        }),
        catchError((err: HttpErrorResponse) => {
          this.segment.track(SegmentEvent.DirectPayoutStripeOauthFailedToConnect, {
            code,
          });
          return of({ error: true, message: err.error.error });
        })
      );
  }

  createPayoutMethod(request: AdyenPayoutMethodCreateRequest) {
    return this.http.post(`${this.apiUrl}/settings/payout-methods`, request);
  }

  editPayoutMethod(payout: PayoutMethod): Observable<any> {
    return this.http.put(`${this.bookingApiUrl}/payout-methods/${payout.uuid}`, {
      property_scope: payout.property_scope,
      all_properties: payout.all_properties,
      name: payout.name,
      provider: payout.provider,
    });
  }

  editPayoutMethodMonolith(uuid: string, request: PayoutMethodMonolithEditRequest) {
    return this.http.put(`${this.apiUrl}/settings/payout-methods/${uuid}`, request);
  }

  setPayoutMethodForProperty(propertyId: any, newPaymentMethodUuid: string): Observable<any> {
    return this.http.patch(`${this.bookingApiUrl}/payout-methods/${newPaymentMethodUuid}/properties/${propertyId}`, {});
  }

  deletePayoutMethod(uuid: string): Observable<any> {
    return this.http.delete(`${this.bookingApiUrl}/payout-methods/${uuid}`);
  }

  deletePayoutMethodForResource(resourceType: string, resourceId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/settings/payout-methods/for/${resourceType}/${resourceId}`);
  }

  adyenGetVerifyLink(uuid: string) {
    return this.http.get<{ data: { url: string } }>(`${this.bookingApiUrl}/payout-methods/adyen/${uuid}/kyc-url`);
  }

  getAdyenKYCUrl(uuid: string) {
    return this.http.get<{ data: { url: string } }>(`${this.apiUrl}/settings/payout-methods/adyen/${uuid}/kyc-url`, {
      params: {
        return_url: `${window.location.origin}/settings/payouts`,
      },
    });
  }

  getVerifyInvitationLink(uuid: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/settings/payout-methods/${uuid}/link`);
  }

  sendVerifyInvitation(uuid: string, request: PayoutMethodVerifyInvitationRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/settings/payout-methods/${uuid}/invite`, request);
  }

  getPropertiesAndAssociatedPayoutMethods(): Observable<
    Array<Property & { payoutMethod?: { id: string; name: string } }>
  > {
    // First get the current subscription status to determine which payout methods to use
    return this.directSubscriptionService.subscriptionStatus$.pipe(
      switchMap((subscription) => {
        // Determine which provider to filter by based on the plan
        let providerFilter: PayoutMethodProvider | undefined;

        if (subscription?.plan) {
          providerFilter =
            subscription.plan === DirectPlan.Basic ? PayoutMethodProvider.STRIPE_OAUTH : PayoutMethodProvider.ADYEN;
        }
        // If no subscription or plan, we'll fetch all payout methods (providerFilter = undefined)

        // Fetch payout methods with the provider filter
        return this.fetchPayoutMethods(providerFilter);
      }),
      switchMap((payoutMethods) => {
        return this.propertiesService
          .getProperties({
            filterCriteria: [],
            paginate: false,
            transformer: 'simple',
          })
          .pipe(
            map((propertiesResponse) => {
              const properties = propertiesResponse.data || [];

              // Create a map of property ID to payout method for efficient lookup
              const propertyToPayoutMap = new Map<number, { id: string; name: string }>();

              payoutMethods.forEach((payoutMethod) => {
                if (payoutMethod.property_scope && payoutMethod.property_scope.length > 0) {
                  // Add payout method to specific properties in scope
                  payoutMethod.property_scope.forEach((propertyId) => {
                    if (!propertyToPayoutMap.has(propertyId)) {
                      propertyToPayoutMap.set(propertyId, {
                        id: payoutMethod.uuid,
                        name: payoutMethod.name || payoutMethod.derived_name,
                      });
                    }
                  });
                }
              });

              // Enhance properties with payout method information
              return properties.map((property) => {
                const payoutMethod = propertyToPayoutMap.get(property.id);
                return {
                  ...property,
                  payoutMethod: payoutMethod || undefined,
                };
              });
            })
          );
      })
    );
  }

  async checkForOverlappingProperties(payoutMethod: any): Promise<any[]> {
    const overlappingProperties = [];

    // Get a list of the properties that the user wants to associate with this payout method
    let selectedPropertyIds: number[] = payoutMethod.properties || payoutMethod.property_scope;

    // And also fetch a list of all current payout methods, and their property scopes
    const existingPayoutMethods = await this.fetchPayoutMethods().toPromise();

    // Get a list of all properties currently scoped to a payout method
    const propertiesAlreadyScoped = this.mergeDedupe(
      existingPayoutMethods
        .filter((payout) => payout.all_properties === false)
        .filter((payout) => payout.property_scope.length > 0)
        .map((payout) => payout.property_scope)
    );

    // Find myself and exclude any properties already scoped to this payout method - for edit only
    if (payoutMethod.property_scope) {
      const myPayoutMethod = existingPayoutMethods.find((pm) => pm.uuid === payoutMethod.uuid);
      selectedPropertyIds = selectedPropertyIds.filter((id) => !myPayoutMethod.property_scope.includes(id));
    }

    // For each selected property, determine if it already is scoped to another payout method, if it is - we need to warn about it
    const overlappingPropertyIds = selectedPropertyIds.filter((element) => propertiesAlreadyScoped.includes(element));

    if (overlappingPropertyIds.length > 0) {
      await Promise.all(
        overlappingPropertyIds.map(async (propId) => {
          const property = await this.propertiesService.getProperty(propId.toString()).toPromise();

          overlappingProperties.push({
            name: property.name,
            picture: property.picture,
            payout: existingPayoutMethods.find((existingMethod) => existingMethod.property_scope.includes(propId)),
          });
        })
      );

      return overlappingProperties;
    } else {
      return [];
    }
  }

  getTaxInformation(payoutId: string) {
    return this.http.get(`${this.bookingApiUrl}/payout-methods/${payoutId}/tax-information`);
  }

  updateTaxInformation(payoutId: string, payload: any) {
    return this.http.post(`${this.bookingApiUrl}/payout-methods/${payoutId}/tax-information`, payload);
  }

  private mergeDedupe = (arr) => {
    return [...new Set([].concat(...arr))];
  };

  getProducts(): Observable<ProductItem[]> {
    return this.productCatalogService.getProductCategories().pipe(
      map((response) => {
        const categories = response.data || [];
        // Flatten all products from all categories into a single array
        return categories.reduce((allProducts: ProductItem[], category) => {
          return allProducts.concat(category.products || []);
        }, []);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching products:', error);
        return of([]);
      })
    );
  }
}
